import { Box, styled, Typography, useTheme } from '@mui/material';
import { PieChart } from '@mui/x-charts';
import type { FC } from 'react';
import type { ProductSpecificationData } from '../../models';
import { AvaniCard } from './AvaniCard';

type AvaniPieChartCardProps = {
  title: string;
  data?: ProductSpecificationData;
};

const StyledTitle = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  display: 'block',
  textAlign: 'center',
  color: theme.colors.avani.teal
}));

function PieCenterLabel({ label, outOfRange }: { label: string; outOfRange: number }) {
  return (
    <StyledTitle>
      {label} {outOfRange}
    </StyledTitle>
  );
}

export const AvaniPieChartCard: FC<AvaniPieChartCardProps> = ({ data, title }) => {
  const theme = useTheme();

  const chartColors = [theme.palette.aMagenta.main, theme.palette.aTeal.main];

  const chartData: {
    value: number;
    label: string;
  }[] = data
    ? [
        {
          value: data.outOfRangeCount,
          label: data.desc ? data.desc : 'Out'
        },
        {
          value: data.totalCount - data.outOfRangeCount,
          label: 'Ok'
        }
      ]
    : [
        {
          value: 0,
          label: 'No data'
        }
      ];

  const chartLeftOffset = '87px';

  return (
    <AvaniCard
      padded
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        ['&.AvaniCardPadded .MuiTypography-root.MuiTypography-h3']: {
          paddingBottom: 0
        }
      }}
    >
      {data && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minHeight: '250px'
          }}
        >
          <Typography variant={'h3'} component={'div'} fontSize={24} color={theme.colors.avani.teal}>
            {title}
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              justifyContent: 'space-around',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {data?.outOfRangeCount && data.totalCount && (
              <>
                <Box
                  sx={{
                    padding: 0,
                    margin: 0,
                    overflow: 'hidden',
                    marginLeft: chartLeftOffset,
                    marginTop: '24px'
                  }}
                >
                  <PieChart
                    series={[{ data: chartData, innerRadius: 50 }]}
                    height={180}
                    width={320}
                    margin={{
                      top: 0,
                      left: 0
                    }}
                    colors={chartColors}
                    sx={{
                      ['.MuiPieArc-root']: { stroke: 'none' }
                    }}
                    slotProps={{
                      legend: { hidden: true }
                    }}
                  ></PieChart>
                </Box>
                <PieCenterLabel label={data.desc ? data.desc : 'Out of range'} outOfRange={data.outOfRangeCount} />
              </>
            )}
          </Box>
        </Box>
      )}
    </AvaniCard>
  );
};
